import dayjs, { Dayjs } from 'dayjs';
import { SUBMISSION_LIMITS, SubmissionType, WEEK_DAYS, getFileTypeByName } from '../../../model/ser/ser-enums';
import { ISubmissionVehicleResponse } from '../../../model/ser/submission/response/submission-vehicle-response';
import { ISubmissionSubmitItem } from '../../../model/ser/submission/request/submission-submit-request';

// Remessa em montagem na tela de envio (uma por item do lote)
export interface SubmissionDraft {
    key: string;
    title: string;
    instruction: string;
    publishDate: string | null;
    type: SubmissionType;
    files: File[];
    vehicleIds: number[];
    // Mensagens devolvidas pela API para esta remessa no último envio
    apiErrors: string[];
}

export enum SendStep {
    Data = 0,
    Files = 1,
    Vehicles = 2,
    Review = 3,
}

export type DraftStatus = 'ready' | 'pending' | 'error';

export interface DataStepErrors {
    title?: string;
    publishDate?: string;
}

export interface RejectedFile {
    name: string;
    reason: string;
}

export const MESSAGES = {
    titleRequired: 'Informe o título da remessa',
    titleTooLong: `O título deve ter no máximo ${SUBMISSION_LIMITS.titleMaxLength} caracteres`,
    publishDateRequired: 'Informe a data de publicação',
    publishDatePast: 'A data de publicação não pode ser anterior a hoje',
    publishDateWeekend: 'A publicação não pode ser em sábado ou domingo',
    filesRequired: 'Adicione ao menos um arquivo',
    vehiclesRequired: 'Selecione ao menos um veículo de publicação',
    fileNotAllowed: 'tipo de arquivo não permitido',
    fileEmpty: 'arquivo vazio',
    fileTooLarge: `excede o tamanho máximo de ${SUBMISSION_LIMITS.maxFileSizeMb} MB`,
    tooManyFiles: `limite de ${SUBMISSION_LIMITS.maxFilesPerSubmission} arquivos por remessa`,
};

let keySequence = 0;
export function newDraftKey(): string {
    keySequence += 1;
    return `draft-${Date.now()}-${keySequence}`;
}

export function createDraft(key = newDraftKey()): SubmissionDraft {
    return { key, title: '', instruction: '', publishDate: null, type: SubmissionType.Rotina, files: [], vehicleIds: [], apiErrors: [] };
}

// Veículos não contam: o diário oficial próprio já vem marcado em toda remessa nova
export function isDraftEmpty(draft: SubmissionDraft): boolean {
    return !draft.title.trim() && !draft.instruction.trim() && !draft.publishDate && draft.files.length === 0;
}

// Remessas não são publicadas aos sábados e domingos
export function isWeekend(date: Dayjs): boolean {
    return date.day() === 0 || date.day() === 6;
}

export function validateDataStep(draft: SubmissionDraft, now: Dayjs = dayjs()): DataStepErrors {
    const errors: DataStepErrors = {};
    const title = draft.title.trim();
    if (!title) errors.title = MESSAGES.titleRequired;
    else if (title.length > SUBMISSION_LIMITS.titleMaxLength) errors.title = MESSAGES.titleTooLong;

    if (!draft.publishDate) errors.publishDate = MESSAGES.publishDateRequired;
    else if (dayjs(draft.publishDate).isBefore(now, 'day')) errors.publishDate = MESSAGES.publishDatePast;
    else if (isWeekend(dayjs(draft.publishDate))) errors.publishDate = MESSAGES.publishDateWeekend;
    return errors;
}

export function validateFilesStep(draft: SubmissionDraft): string | undefined {
    return draft.files.length === 0 ? MESSAGES.filesRequired : undefined;
}

export function validateVehiclesStep(draft: SubmissionDraft): string | undefined {
    return draft.vehicleIds.length === 0 ? MESSAGES.vehiclesRequired : undefined;
}

export function isStepValid(draft: SubmissionDraft, step: SendStep, now?: Dayjs): boolean {
    switch (step) {
        case SendStep.Data: return Object.keys(validateDataStep(draft, now)).length === 0;
        case SendStep.Files: return !validateFilesStep(draft);
        case SendStep.Vehicles: return !validateVehiclesStep(draft);
        default: return true;
    }
}

// Primeira etapa com pendência (Revisão quando tudo está preenchido)
export function firstInvalidStep(draft: SubmissionDraft, now?: Dayjs): SendStep {
    return [SendStep.Data, SendStep.Files, SendStep.Vehicles].find((step) => !isStepValid(draft, step, now)) ?? SendStep.Review;
}

export function getDraftStatus(draft: SubmissionDraft, now?: Dayjs): DraftStatus {
    if (draft.apiErrors.length > 0) return 'error';
    return firstInvalidStep(draft, now) === SendStep.Review ? 'ready' : 'pending';
}

// Adiciona os arquivos válidos e devolve os recusados com o motivo (os válidos nunca são descartados)
export function addFiles(current: File[], incoming: File[]): { files: File[]; rejected: RejectedFile[] } {
    const files = [...current];
    const rejected: RejectedFile[] = [];
    const maxBytes = SUBMISSION_LIMITS.maxFileSizeMb * 1024 * 1024;

    incoming.forEach((file) => {
        let reason: string | undefined;
        if (!getFileTypeByName(file.name)) reason = MESSAGES.fileNotAllowed;
        else if (file.size === 0) reason = MESSAGES.fileEmpty;
        else if (file.size > maxBytes) reason = MESSAGES.fileTooLarge;
        else if (files.length >= SUBMISSION_LIMITS.maxFilesPerSubmission) reason = MESSAGES.tooManyFiles;

        if (reason) rejected.push({ name: file.name, reason });
        else files.push(file);
    });

    return { files, rejected };
}

export function totalFilesSize(drafts: SubmissionDraft[]): number {
    return drafts.reduce((total, draft) => total + draft.files.reduce((sum, file) => sum + file.size, 0), 0);
}

// A cópia mantém data, tipo, instruções e veículos; arquivos são escolhidos de novo
export function duplicateDraft(draft: SubmissionDraft, key = newDraftKey()): SubmissionDraft {
    return { ...draft, key, title: draft.title.trim() ? `${draft.title.trim()} (cópia)` : '', files: [], vehicleIds: [...draft.vehicleIds], apiErrors: [] };
}

export function toSubmitItem(draft: SubmissionDraft): ISubmissionSubmitItem {
    return {
        title: draft.title.trim(),
        instruction: draft.instruction.trim() || undefined,
        publishDate: draft.publishDate as string,
        type: draft.type,
        vehicleIds: draft.vehicleIds,
        files: draft.files,
    };
}

export interface VehicleDeadline {
    // O veículo publica no dia da semana da data escolhida (sem dias cadastrados = qualquer dia)
    publishesOnDay: boolean;
    // Prazo-limite de envio para a data escolhida; null sem data ou sem regra de prazo
    deadline: Dayjs | null;
    expired: boolean;
    allowedDays: string;
}

export function weekDayId(date: Dayjs): number {
    return date.day() + 1;
}

export function weekDayName(date: Dayjs): string {
    return WEEK_DAYS[date.day()].name;
}

export function formatAllowedDays(weekDays?: number[]): string {
    if (!weekDays || weekDays.length === 0) return 'Todos os dias';
    return WEEK_DAYS.filter((day) => weekDays.includes(day.id)).map((day) => day.short).join(', ');
}

// Prazo do veículo para a data escolhida; veículo com prazo encerrado não pode ser selecionado
export function computeVehicleDeadline(vehicle: ISubmissionVehicleResponse, publishDate: string | null, now: Dayjs = dayjs()): VehicleDeadline {
    const allowedDays = formatAllowedDays(vehicle.weekDays);
    if (!publishDate) return { publishesOnDay: true, deadline: null, expired: false, allowedDays };

    const date = dayjs(publishDate);
    const publishesOnDay = !vehicle.weekDays?.length || vehicle.weekDays.includes(weekDayId(date));
    if (!vehicle.limitTime && vehicle.daysBefore === undefined) return { publishesOnDay, deadline: null, expired: false, allowedDays };

    const [hours, minutes] = (vehicle.limitTime || '23:59').split(':').map(Number);
    const deadline = date.startOf('day').subtract(vehicle.daysBefore ?? 0, 'day').hour(hours || 0).minute(minutes || 0).second(0);
    return { publishesOnDay, deadline, expired: now.isAfter(deadline), allowedDays };
}

// "qui., 15/10 às 17:00"
export function formatDeadline(deadline: Dayjs): string {
    return `${WEEK_DAYS[deadline.day()].short}, ${deadline.format('DD/MM')} às ${deadline.format('HH:mm')}`;
}

export function isVehicleExpired(vehicle: ISubmissionVehicleResponse, publishDate: string | null, now: Dayjs = dayjs()): boolean {
    return computeVehicleDeadline(vehicle, publishDate, now).expired;
}

// Ids dos veículos que entram em toda remessa (diário oficial próprio)
export function defaultVehicleIds(vehicles: ISubmissionVehicleResponse[]): number[] {
    return vehicles.filter((v) => v.isDefault).map((v) => v.id);
}

// Mantém só veículos da entidade e garante os padrão (sem duplicar)
export function withDefaultVehicles(vehicleIds: number[], vehicles: ISubmissionVehicleResponse[]): number[] {
    const ids = new Set(vehicles.map((v) => v.id));
    return Array.from(new Set([...defaultVehicleIds(vehicles), ...vehicleIds.filter((id) => ids.has(id))]));
}

// Diário próprio com prazo encerrado para a data: ele não pode sair da remessa, então a data é que precisa mudar
export function expiredDefaultVehicles(draft: SubmissionDraft, vehicles: ISubmissionVehicleResponse[], now: Dayjs = dayjs()): ISubmissionVehicleResponse[] {
    return vehicles.filter((v) => v.isDefault && draft.vehicleIds.includes(v.id) && isVehicleExpired(v, draft.publishDate, now));
}

// Tira da remessa os veículos cujo prazo já encerrou para a data dela (devolve os nomes removidos); o padrão nunca sai
export function removeExpiredVehicles(draft: SubmissionDraft, vehicles: ISubmissionVehicleResponse[], now: Dayjs = dayjs()): { vehicleIds: number[]; removed: string[] } {
    const expired = vehicles.filter((v) => !v.isDefault && draft.vehicleIds.includes(v.id) && isVehicleExpired(v, draft.publishDate, now));
    const expiredIds = new Set(expired.map((v) => v.id));
    return { vehicleIds: draft.vehicleIds.filter((id) => !expiredIds.has(id)), removed: expired.map((v) => v.name || `Veículo ${v.id}`) };
}
